import { colors, getHeight, getSpacing } from '@cabonline/ui-shared';
import { Text } from '@cabonline/ui-web';
import styled from '@emotion/styled';
import { Menu as MenuIcon, PersonOutlineRounded } from '@mui/icons-material';
import { useAuth, useLocale } from '@multiplatform-apps/service-trips';
import { FC, useCallback } from 'react';
import { NavLink } from 'react-router-dom';

import { paths } from '../../../constants';
import { ENABLE_NOTIFICATIONS } from '../../../constants/feature-toggles';
import { useMenu } from '../../../context';
import { Menu } from '../menu/menu';
import { NotificationBell } from '../notification-bell';

const StyledMobileMenuContainer = styled.div`
  display: flex;
  height: ${getHeight('m')};
  align-items: center;
  justify-content: center;
`;

interface StyledNavLinkProps {
  isMenuOpen?: boolean;
}

const StyledMobileNavLink = styled(NavLink, {
  shouldForwardProp: (prop) => prop !== 'isMenuOpen',
})<StyledNavLinkProps>`
  text-decoration: none;
  background-color: ${colors.blackHover};
  height: ${getHeight('m')};
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: center;
  gap: ${getSpacing('xxs')};
  &.active {
    background-color: ${(props) => !props.isMenuOpen && colors.primary};
  }
`;

interface StyledMenuButtonProps {
  isMenuOpen?: boolean;
}

const StyledMenuButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'isMenuOpen',
})<StyledMenuButtonProps>`
  text-decoration: none;
  border: none;
  background-color: ${(props) =>
    props.isMenuOpen ? colors.primary : colors.blackHover};
  height: ${getHeight('m')};
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: center;
  gap: ${getSpacing('xxs')};
  cursor: pointer;
`;

const StyledBellSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${colors.blackHover};
  height: ${getHeight('m')};
  padding: 0 ${getSpacing('s')};
  margin-left: auto;
  flex-shrink: 0;
`;

const useMobileNavbar = () => {
  const { isAuthenticated, identity } = useAuth();
  const { t } = useLocale();
  const { isMenuOpen, setIsMenuOpen } = useMenu();

  const toggleMenu = useCallback(() => setIsMenuOpen(!isMenuOpen), [isMenuOpen, setIsMenuOpen]);
  const closeMenu = useCallback(() => setIsMenuOpen(false), [setIsMenuOpen]);
  const displayName = identity?.firstName || identity?.displayName;

  return { isAuthenticated, t, isMenuOpen, toggleMenu, closeMenu, displayName };
};

const resolveMenuIconColor = (isMenuOpen: boolean) =>
  isMenuOpen ? colors.black : colors.primary;

const resolveMenuLabelColor = (isMenuOpen: boolean) =>
  isMenuOpen ? colors.black : colors.white;

const resolveProfileIconColor = (isActive: boolean, isMenuOpen: boolean) =>
  isActive && !isMenuOpen ? colors.black : colors.primary;

const resolveProfileTextColor = (isActive: boolean, isMenuOpen: boolean) =>
  isActive && !isMenuOpen ? colors.black : colors.white;

interface ProfileLinkProps {
  isMenuOpen: boolean;
  displayName: string | undefined;
  onClose: () => void;
}

const ProfileLink: FC<ProfileLinkProps> = ({ isMenuOpen, displayName, onClose }) => (
  <StyledMobileNavLink isMenuOpen={isMenuOpen} to={paths.profile.root} onClick={onClose}>
    {({ isActive }) => (
      <>
        <PersonOutlineRounded htmlColor={resolveProfileIconColor(isActive, isMenuOpen)} />
        <Text weight="extraBold" size="m" color={resolveProfileTextColor(isActive, isMenuOpen)}>
          {displayName}
        </Text>
      </>
    )}
  </StyledMobileNavLink>
);

export const MobileNavbar: FC = () => {
  const { isAuthenticated, t, isMenuOpen, toggleMenu, closeMenu, displayName } =
    useMobileNavbar();

  return (
    <>
      <StyledMobileMenuContainer>
        <StyledMenuButton
          onClick={toggleMenu}
          isMenuOpen={isMenuOpen}
          role="button"
          aria-label="Menu button"
        >
          <>
            <MenuIcon htmlColor={resolveMenuIconColor(isMenuOpen)} />
            <Text weight="extraBold" size="m" color={resolveMenuLabelColor(isMenuOpen)}>
              {t('mobileMenu.label')}
            </Text>
          </>
        </StyledMenuButton>
        {isAuthenticated && (
          <ProfileLink isMenuOpen={isMenuOpen} displayName={displayName} onClose={closeMenu} />
        )}
        {isAuthenticated && ENABLE_NOTIFICATIONS && (
          <StyledBellSlot>
            <NotificationBell />
          </StyledBellSlot>
        )}
      </StyledMobileMenuContainer>
      {isMenuOpen && <Menu closeMenu={closeMenu} />}
    </>
  );
};
